import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Cake, ChevronLeft, ChevronRight, Printer, Download, Check, LayoutGrid, List, Pencil, X } from 'lucide-react';
import { toPng } from 'html-to-image';
import { getBirthdaysMonth, BirthdayMonth, greetMember, ungreetMember } from '../members/api';
import PersonAvatar from '../../components/PersonAvatar';
import { teacherPhotoUrl } from '../teachers/api';
import { getSetting, putSetting } from '../../services/settings';
import { readCache, writeCache } from '../../services/localCache';
import { getWhatsAppLink, fillTemplate, TEMPLATE_VARS } from '../../utils/whatsapp';
import ClassChips from '../../components/ClassChips';
import WhatsAppIcon from '../../components/WhatsAppIcon';
import { Skeleton } from '../../components/Skeleton';
import EmptyState from '../../components/EmptyState';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const DEFAULT_MSG = '¡Feliz cumpleaños, {nombre}! 🎉💛 Todo el equipo de Let\'s Move te desea un día increíble.';

function msgFor(tpl: string, b: BirthdayMonth) {
  return fillTemplate(tpl, { nombre: b.firstname, apellido: b.lastname, edad: b.turns });
}

type Handlers = { onGreet: (b: BirthdayMonth) => void; onToggle: (b: BirthdayMonth) => void; month: number; template: string };

/** Ruta a la ficha (alumno o profe) según el rol del cumpleañero. */
const profileTo = (b: BirthdayMonth) => (b.role !== 'student' ? `/profesores/${b.account_id}` : `/alumnos/${b.account_id}`);

/** Avatar + nombre clicables → ficha. */
function PersonLink({ b, children }: { b: BirthdayMonth; children: ReactNode }) {
  return (
    <Link to={profileTo(b)} title="Abrir ficha" className="flex min-w-0 items-center gap-2.5 transition hover:opacity-80">
      {children}
    </Link>
  );
}

function EstadoChip({ b, onToggle }: { b: BirthdayMonth; onToggle: (b: BirthdayMonth) => void }) {
  return b.greeted ? (
    <button onClick={() => onToggle(b)} title="Quitar felicitado"
      className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
      <Check size={12} /> Felicitado
    </button>
  ) : (
    <button onClick={() => onToggle(b)} title="Marcar como felicitado"
      className="rounded-full bg-surface-2 px-2 py-0.5 text-xs font-medium text-content-3 hover:text-content-2">
      Pendiente
    </button>
  );
}

function FelicitarBtn({ b, onGreet, template }: { b: BirthdayMonth; onGreet: (b: BirthdayMonth) => void; template: string }) {
  const wa = getWhatsAppLink(b.phone_prefix, b.phone_number, msgFor(template, b));
  if (!wa) return <span className="text-xs text-content-3">sin tel.</span>;
  return (
    <a href={wa} target="_blank" rel="noreferrer" onClick={() => onGreet(b)}
      className="inline-flex items-center gap-1.5 rounded-md bg-[#25D366] px-2.5 py-1 text-xs font-semibold text-white transition hover:bg-[#1ebe5b]">
      <WhatsAppIcon size={13} /> Felicitar
    </a>
  );
}

function Avatar({ b }: { b: BirthdayMonth }) {
  const esProfe = b.role !== 'student';   // profesor o equipo (staff): foto de usuario
  return <PersonAvatar
    contactId={esProfe ? null : b.account_id}
    photoSrc={esProfe ? teacherPhotoUrl(b.account_id) : undefined}
    hasPhoto={b.has_photo} name={b.firstname} lastName={b.lastname}
    size="h-11 w-11" initialClass="text-base" className="ring-1 ring-line rounded-full" />;
}

function ListSection({ title, rows, highlight, muted, onGreet, onToggle, month, template }: Handlers & {
  title: string; rows: BirthdayMonth[]; highlight?: boolean; muted?: boolean;
}) {
  const mon = MONTHS[month - 1].slice(0, 3);
  return (
    <section>
      <h3 className={`mb-2 flex items-center gap-2 text-sm font-semibold ${highlight ? 'text-accent-ink' : 'text-content-2'}`}>
        {title} <span className="font-normal text-content-3">({rows.length})</span>
      </h3>
      <div className={`overflow-hidden rounded-2xl border bg-surface shadow-sm ${highlight ? 'border-accent/50' : 'border-line'} ${muted ? 'opacity-70' : ''}`}>
        {/* Tabla (escritorio) */}
        <table className="hidden w-full text-sm sm:table">
          <thead>
            <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-content-3">
              <th className="px-4 py-2.5 font-medium">Persona</th>
              <th className="px-4 py-2.5 font-medium">Cumpleaños</th>
              <th className="px-4 py-2.5 font-medium">Edad</th>
              <th className="px-4 py-2.5 font-medium">Estado</th>
              <th className="px-4 py-2.5 text-right font-medium">Acción</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.account_id} className="border-b border-line/60 last:border-0 even:bg-surface-2/40">
                <td className="px-4 py-2.5">
                  <PersonLink b={b}>
                    <Avatar b={b} />
                    <span className="min-w-0">
                      <span className="flex items-center gap-2 font-medium text-content">
                        {b.firstname} {b.lastname}
                        {b.role !== 'student' && <span className="rounded-full bg-brand-blue/15 px-1.5 py-0.5 text-[10px] font-semibold text-brand-blue">{b.role === 'teacher' ? 'Profe' : 'Equipo'}</span>}
                      </span>
                      <ClassChips classes={b.classes} max={3} />
                    </span>
                  </PersonLink>
                </td>
                <td className="px-4 py-2.5 text-content-2">{b.day} de {mon}</td>
                <td className="px-4 py-2.5 text-content-2">{b.turns} años</td>
                <td className="px-4 py-2.5"><EstadoChip b={b} onToggle={onToggle} /></td>
                <td className="px-4 py-2.5 text-right"><FelicitarBtn b={b} onGreet={onGreet} template={template} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* Tarjetas (móvil) */}
        <ul className="divide-y divide-line/60 sm:hidden">
          {rows.map((b) => (
            <li key={b.account_id} className="p-3">
              <div className="flex items-center justify-between gap-3">
                <PersonLink b={b}>
                  <Avatar b={b} />
                  <span className="min-w-0">
                    <span className="flex items-center gap-1.5 font-medium text-content">
                      <span className="truncate">{b.firstname} {b.lastname}</span>
                      {b.role !== 'student' && <span className="shrink-0 rounded-full bg-brand-blue/15 px-1.5 py-0.5 text-[10px] font-semibold text-brand-blue">{b.role === 'teacher' ? 'Profe' : 'Equipo'}</span>}
                    </span>
                    <span className="block text-xs text-content-3">{b.day} de {mon} · {b.turns} años</span>
                    <ClassChips classes={b.classes} max={2} />
                  </span>
                </PersonLink>
                <EstadoChip b={b} onToggle={onToggle} />
              </div>
              <div className="mt-2 flex justify-end"><FelicitarBtn b={b} onGreet={onGreet} template={template} /></div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/** Modal para editar la plantilla del mensaje de felicitación (con variables + preview). */
function MessageEditor({ initial, onClose, onSaved }: { initial: string; onClose: () => void; onSaved: (v: string) => void }) {
  const toast = useToast();
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);

  function insertVar(token: string) {
    const ta = taRef.current;
    if (!ta) { setDraft((d) => d + token); return; }
    const start = ta.selectionStart, end = ta.selectionEnd;
    const next = draft.slice(0, start) + token + draft.slice(end);
    setDraft(next);
    requestAnimationFrame(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = start + token.length; });
  }

  async function save() {
    const v = draft.trim();
    if (!v) { toast.error('El mensaje no puede estar vacío'); return; }
    setSaving(true);
    try {
      const r = await putSetting('birthday_message', v);
      onSaved(r.value);
      toast.success('Mensaje guardado');
      onClose();
    } catch (e: any) { toast.error(e?.message || 'No se pudo guardar'); }
    finally { setSaving(false); }
  }

  const preview = fillTemplate(draft, { nombre: 'María', apellido: 'López', edad: 25 });

  return createPortal(
    <div className="modal-overlay fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="modal-panel w-full max-w-lg rounded-2xl border border-line bg-surface p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-content">Mensaje de felicitación</h3>
          <button onClick={onClose} className="grid h-8 w-8 place-items-center rounded-lg text-content-3 hover:bg-surface-2"><X size={18} /></button>
        </div>
        <textarea ref={taRef} value={draft} onChange={(e) => setDraft(e.target.value)} rows={4}
          className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30"
          placeholder="Escribe el mensaje…" />
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-content-3">Insertar:</span>
          {TEMPLATE_VARS.map((v) => (
            <button key={v.token} onClick={() => insertVar(v.token)}
              className="rounded-md border border-line bg-surface-2 px-2 py-0.5 text-xs font-medium text-content-2 hover:bg-surface-2/70">
              {v.label}
            </button>
          ))}
        </div>
        <div className="mt-3 rounded-lg border border-line bg-surface-2/50 p-3">
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-content-3">Vista previa</div>
          <p className="whitespace-pre-wrap text-sm text-content">{preview || '—'}</p>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-line bg-surface px-4 py-2 text-sm text-content-2 transition hover:bg-surface-2">Cancelar</button>
          <button onClick={save} disabled={saving} className="rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90 disabled:opacity-50">
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function BirthdaysMonthPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [view, setView] = useState<'lista' | 'muro'>('lista');
  const [month, setMonth] = useState<number>(new Date().getMonth() + 1);
  // Pinta al instante lo último conocido del mes (caché local) y refresca por detrás.
  const [rows, setRows] = useState<BirthdayMonth[] | null>(
    () => readCache<BirthdayMonth[]>(`bmonth:${new Date().getMonth() + 1}`) ?? null);
  const [downloading, setDownloading] = useState(false);
  const [template, setTemplate] = useState(DEFAULT_MSG);
  const [editing, setEditing] = useState(false);
  const muroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const cached = readCache<BirthdayMonth[]>(`bmonth:${month}`);
    setRows(cached ?? null);
    getBirthdaysMonth(month).then((r) => {
      setRows(r.data);
      writeCache(`bmonth:${month}`, r.data);
    }).catch(() => setRows((prev) => prev ?? []));
  }, [month]);

  useEffect(() => {
    getSetting('birthday_message').then((r) => setTemplate(r.value || DEFAULT_MSG)).catch(() => {});
  }, []);

  const prev = () => setMonth((m) => (m === 1 ? 12 : m - 1));
  const next = () => setMonth((m) => (m === 12 ? 1 : m + 1));

  const now = new Date();
  const todayM = now.getMonth() + 1, todayD = now.getDate();
  const isCurrentMonth = month === todayM;

  function setGreeted(id: number, val: boolean) {
    setRows((rs) => (rs ? rs.map((r) => (r.account_id === id ? { ...r, greeted: val ? 1 : 0 } : r)) : rs));
  }
  function onGreet(b: BirthdayMonth) {
    if (b.greeted) return;
    setGreeted(b.account_id, true);
    greetMember(b.account_id).catch(() => setGreeted(b.account_id, false));
  }
  function onToggle(b: BirthdayMonth) {
    const val = !b.greeted;
    setGreeted(b.account_id, val);
    (val ? greetMember(b.account_id) : ungreetMember(b.account_id)).catch(() => setGreeted(b.account_id, !val));
  }

  async function downloadPng() {
    if (!muroRef.current) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(muroRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        filter: (node) => !(node instanceof HTMLElement && node.classList?.contains('no-export')),
        backgroundColor: getComputedStyle(muroRef.current).backgroundColor || undefined,
      });
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `cumpleanos-${MONTHS[month - 1].toLowerCase()}.png`;
      a.click();
    } catch (e) {
      console.error('No se pudo generar la imagen', e);
    } finally {
      setDownloading(false);
    }
  }

  const handlers = { onGreet, onToggle, month, template };
  const hoy = isCurrentMonth && rows ? rows.filter((b) => b.day === todayD) : [];
  const prox = rows ? (isCurrentMonth ? rows.filter((b) => b.day > todayD) : rows) : [];
  const pasados = isCurrentMonth && rows ? rows.filter((b) => b.day < todayD) : [];

  const segBtn = (active: boolean) =>
    `inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition ${active ? 'bg-accent text-on-accent' : 'text-content-2 hover:bg-surface-2'}`;

  return (
    // Ruta densa (1600px) — el ancho lo gobierna AppLayout, sin tope interno.
    <div>
      {/* Controles (no se imprimen) */}
      <div className="no-print mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 font-display text-2xl font-bold text-content">
          <Cake className="text-brand-blue" size={24} /> Cumpleaños del mes
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-line bg-surface p-0.5">
            <button onClick={() => setView('lista')} className={segBtn(view === 'lista')}><List size={15} /> Lista</button>
            <button onClick={() => setView('muro')} className={segBtn(view === 'muro')}><LayoutGrid size={15} /> Muro</button>
          </div>
          <div className="flex items-center rounded-lg border border-line bg-surface">
            <button onClick={prev} className="grid h-9 w-9 place-items-center text-content-2 hover:bg-surface-2"><ChevronLeft size={18} /></button>
            <span className="min-w-[110px] px-2 text-center text-sm font-semibold text-content">{MONTHS[month - 1]}</span>
            <button onClick={next} className="grid h-9 w-9 place-items-center text-content-2 hover:bg-surface-2"><ChevronRight size={18} /></button>
          </div>
          {isAdmin && (
            <button onClick={() => setEditing(true)} title="Editar el mensaje de felicitación"
              className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-2 text-sm font-medium text-content-2 transition hover:bg-surface-2">
              <Pencil size={15} /> Editar mensaje
            </button>
          )}
          {view === 'muro' && (
            <>
              <button onClick={downloadPng} disabled={downloading || !rows || rows.length === 0}
                className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-2 text-sm font-medium text-content-2 transition hover:bg-surface-2 disabled:opacity-50">
                <Download size={16} /> {downloading ? 'Generando…' : 'Descargar imagen'}
              </button>
              <button onClick={() => window.print()}
                className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-3 py-2 text-sm font-semibold text-on-accent transition hover:opacity-90">
                <Printer size={16} /> Imprimir
              </button>
            </>
          )}
        </div>
      </div>

      {/* ===== VISTA LISTA (operativa) ===== */}
      {view === 'lista' ? (
        rows === null ? (
          <Skeleton className="h-80 w-full rounded-2xl" />
        ) : rows.length === 0 ? (
          <EmptyState icon={Cake} title="Sin cumpleaños este mes" subtitle={`Nadie cumple años en ${MONTHS[month - 1]}.`} />
        ) : (
          <div className="space-y-5">
            {hoy.length > 0 && <ListSection title="🎉 Cumplen hoy" rows={hoy} highlight {...handlers} />}
            {prox.length > 0 && <ListSection title={isCurrentMonth ? 'Próximos días del mes' : `Cumpleaños de ${MONTHS[month - 1]}`} rows={prox} {...handlers} />}
            {pasados.length > 0 && <ListSection title="Ya cumplieron este mes" rows={pasados} muted {...handlers} />}
          </div>
        )
      ) : (
        /* ===== VISTA MURO (para compartir / PNG) ===== */
        <div ref={muroRef} className="print-area rounded-2xl border border-line bg-surface p-6 shadow-sm">
          <div className="mb-5 text-center">
            <h2 className="font-display text-2xl font-bold text-content">🎂 Cumpleaños de {MONTHS[month - 1]}</h2>
            <p className="text-sm text-content-3">Let's Move Dance &amp; Fitness</p>
          </div>

          {rows === null ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
              {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-40 w-full rounded-xl" />)}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState icon={Cake} title="Sin cumpleaños este mes" subtitle={`Nadie cumple años en ${MONTHS[month - 1]}.`} />
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
              {rows.map((b) => {
                const wa = getWhatsAppLink(b.phone_prefix, b.phone_number, msgFor(template, b));
                return (
                  <div key={b.account_id} className="relative flex flex-col items-center rounded-xl border border-line bg-surface-2/40 p-3 text-center">
                    {b.role !== 'student' && (
                      <span className="absolute right-2 top-2 rounded-full bg-brand-blue/15 px-1.5 py-0.5 text-[10px] font-semibold text-brand-blue">{b.role === 'teacher' ? 'Profe' : 'Equipo'}</span>
                    )}
                    <Link to={profileTo(b)} title="Abrir ficha" className="flex flex-col items-center transition hover:opacity-80">
                      <PersonAvatar
                        contactId={b.role !== 'student' ? null : b.account_id}
                        photoSrc={b.role !== 'student' ? teacherPhotoUrl(b.account_id) : undefined}
                        hasPhoto={b.has_photo} name={b.firstname} lastName={b.lastname}
                        size="h-20 w-20" initialClass="text-2xl" className="ring-2 ring-accent/40 rounded-full" />
                      <div className="mt-2 text-sm font-semibold leading-tight text-content">{b.firstname} {b.lastname}</div>
                    </Link>
                    <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-xs font-bold text-on-accent">
                      {b.day} de {MONTHS[month - 1].slice(0, 3)}
                    </div>
                    <div className="mt-1 text-xs text-content-3">cumple {b.turns} años</div>
                    {wa && (
                      <a href={wa} target="_blank" rel="noreferrer" onClick={() => onGreet(b)}
                        className="no-export no-print mt-2 inline-flex items-center gap-1.5 rounded-md bg-[#25D366] px-2.5 py-1 text-xs font-semibold text-white transition hover:bg-[#1ebe5b]">
                        <WhatsAppIcon size={13} /> Felicitar
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <p className="mt-5 text-center text-xs text-content-3">{rows?.length || 0} cumpleañeros · ¡Felicítalos! 💛</p>
        </div>
      )}

      {editing && <MessageEditor initial={template} onClose={() => setEditing(false)} onSaved={setTemplate} />}
    </div>
  );
}
